import { Alert, AutoComplete, Button, Cascader, Col, Form, Image, Input, Radio, Row, Select, Space } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    BaseItem as ZhBaseItem,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    Upload as ZhUpload,
    ZhMap,
    type UploadRequestOptions
} from "zhihao-ui-react";
import type { File as FileEntity, TiandituVo } from "@zhihaoscm/entity";
import { ENTERPRISE_SHOW_LOCATION, ENTERPRISE_SUPPLIER_TYPE } from "@zhihaoscm/meta";

import request from "@/api";
import { fileService } from "@/api/services/fileService";
import { sliceGbCode } from "@/views/pages/logistics/wharf";
import { validatePhone } from "@/utils/validate";
import DetailPageHeader from "@/views/pages/purchasingManage/components/detailPageHeader";
import { loadGroupCompanies as loadGroupCompanyOptions } from "./SupplierPage";
import type { GroupCompanyOption, SupplierFormValues, SupplierRow } from "./types";
import "../productManagement/ConsignmentFormPage.scss";

export interface SupplierFormPageProps {
    loadGroupCompanies?: (name: string) => Promise<GroupCompanyOption[]>;
    loadAreaOptions?: () => Promise<SupplierAreaOption[]>;
    loadAddressSuggestions?: (keyword: string, regionCode?: string) => Promise<SupplierAddressSuggestion[]>;
    loadSupplier?: (id: number | string) => Promise<SupplierRow>;
    loadSupplierFiles?: (ids: number[]) => Promise<FileEntity[]>;
    uploadSupplierImage?: (file: File) => Promise<FileEntity>;
    saveSupplier?: (id: number | string | undefined, values: SupplierFormValues) => Promise<void>;
}

interface SupplierAreaOption {
    value: string;
    label: string;
    children?: SupplierAreaOption[];
}

interface SupplierAddressSuggestion {
    value: string;
    address?: string;
    lonlat?: string;
    countyCode?: string;
}

type SupplierImageField = "logoFileId" | "businessFileId" | "mobileSideFileId" | "pcSideFileId";

const SUPPLIER_IMAGE_FIELDS: SupplierImageField[] = [
    "logoFileId",
    "businessFileId",
    "mobileSideFileId",
    "pcSideFileId"
];

const SUPPLIER_IMAGE_FIELD_LABELS: Record<SupplierImageField, string> = {
    logoFileId: "企业 Logo 文件 ID",
    businessFileId: "营业执照文件 ID",
    mobileSideFileId: "移动端首页广告文件 ID",
    pcSideFileId: "PC端首页广告文件 ID"
};

// Element Plus 的 xl 断点从 1920px 起，1440px 视觉基准仍保持三列；用 CSS 在 1920px 再切换四列。
const RESPONSIVE_FORM_COL = { xs: 24, sm: 24, md: 12, lg: 8, xl: 8 } as const;

/** 复用旧 Vue ImgList 的 2MB 图片上传限制。 */
function validateSupplierImage(file: File): boolean {
    const validType = ["image/jpeg", "image/png"].includes(file.type.toLowerCase());
    const validSize = file.size <= 2 * 1024 * 1024;
    if (!validType || !validSize) {
        window.dispatchEvent(
            new CustomEvent("zh-upload-error", {
                detail: !validType ? "图片格式不符合要求" : "图片大小不能超过 2M"
            })
        );
    }
    return validType && validSize;
}

/** 将 zhihao Upload 的文件对象提交给旧版文件服务。 */
async function uploadSupplierImage(file: File): Promise<FileEntity> {
    const formData = new FormData();
    formData.append("file", file);
    return fileService.uploadFile(formData);
}

/** 按旧版 ImgList 的文件 ID 批量加载编辑态预览。 */
async function loadSupplierFiles(ids: number[]): Promise<FileEntity[]> {
    return ids.length ? fileService.getFileByIds(ids) : [];
}

/** 复用 Vue 供应商详情接口，为编辑表单回填数据。 */
export async function loadSupplier(id: number | string): Promise<SupplierRow> {
    const response = await request.service.get<SupplierRow>(`/admin/supplier-admin/find/${id}`);
    return response.data;
}

/** 复用 Vue areaLib 的静态行政区数据，延迟加载以避免表单首屏阻塞。 */
export async function loadSupplierAreaOptions(): Promise<SupplierAreaOption[]> {
    const response = await fetch("https://static.zhihaoscm.cn/map/areas.json");
    if (!response.ok) throw new Error("区域数据加载失败");
    const data = (await response.json()) as Array<{
        id: string;
        name: string;
        childs?: Array<{ id: string; name: string; childs?: unknown[] }>;
    }>;
    const toOptions = (items: typeof data): SupplierAreaOption[] =>
        items.map(item => ({
            value: item.id,
            label: item.name,
            children: item.childs ? toOptions(item.childs as typeof data) : undefined
        }));
    return toOptions(data);
}

/** 复用 Vue 供应商地址联想的天地图查询参数和经纬度结果。 */
export async function loadSupplierAddressSuggestions(
    keyword: string,
    regionCode = ""
): Promise<SupplierAddressSuggestion[]> {
    if (!keyword.trim()) return [];
    const { data: result } = await request.service.post<TiandituVo>("/admin/tianditu/search?type=query", {
        keyWord: keyword.trim(),
        mapBound: "-180,-90,180,90",
        level: 18,
        queryType: regionCode.trim() ? "7" : "4",
        show: "2",
        start: "0",
        count: "10",
        ...(regionCode.trim() ? { specify: regionCode.trim() } : {})
    });
    const pois = (result.pois || []).map(item => ({
        value: item.address,
        address: item.address,
        lonlat: item.lonlat,
        countyCode: item.countyCode
    }));
    const suggests = (result.suggests || []).map(item => ({
        value: item.name,
        address: item.address,
        lonlat: item.lonlat,
        countyCode: item.gbCode
    }));
    return [...suggests, ...pois];
}

/** 复用 Vue 供应商新增/修改接口。 */
export async function saveSupplier(id: number | string | undefined, values: SupplierFormValues): Promise<void> {
    if (id == null) {
        await request.service.post("/admin/supplier-admin/create", values);
    } else {
        await request.service.put(`/admin/supplier-admin/update/${id}`, values);
    }
}

/** 将 Vue 供应商编辑返回的类型和展示位置枚举统一为表单使用的数字。 */
function normalizeSupplierFormValues(supplier: SupplierFormValues): SupplierFormValues {
    return {
        ...supplier,
        type: supplier.type == null ? 1 : Number(supplier.type),
        showLocation: supplier.showLocation == null ? undefined : Number(supplier.showLocation)
    };
}

/** 将 Vue 地图弹窗使用的经纬度文本转换为 zhihao 地图中心点。 */
function parseMapCenter(latLon?: string): [number, number] | undefined {
    const coordinates = latLon?.split(",").map(value => Number(value.trim()));
    if (!coordinates || coordinates.length < 2 || coordinates.some(value => !Number.isFinite(value))) return undefined;
    return [coordinates[0], coordinates[1]];
}

/** 供应商新增/编辑页，覆盖核心供应商字段并保留 Vue 保存契约。 */
export function SupplierFormPage({
    loadGroupCompanies: loadGroups = loadGroupCompanyOptions,
    loadAreaOptions: loadAreas = loadSupplierAreaOptions,
    loadAddressSuggestions: loadAddresses = loadSupplierAddressSuggestions,
    loadSupplier: load = loadSupplier,
    loadSupplierFiles: loadFiles = loadSupplierFiles,
    uploadSupplierImage: uploadImage = uploadSupplierImage,
    saveSupplier: save = saveSupplier
}: SupplierFormPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const id = new URLSearchParams(location.search).get("id") || undefined;
    const [form] = Form.useForm<SupplierFormValues>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isRelateGroup, setIsRelateGroup] = useState(false);
    const [groupSearch, setGroupSearch] = useState("");
    const [selectedGroupName, setSelectedGroupName] = useState<string | undefined>();
    const [groupOptions, setGroupOptions] = useState<GroupCompanyOption[]>([]);
    const [areaOptions, setAreaOptions] = useState<SupplierAreaOption[]>([]);
    const [areaLoading, setAreaLoading] = useState(false);
    const [areaLoaded, setAreaLoaded] = useState(false);
    const [enterpriseTags, setEnterpriseTags] = useState<Array<{ value: string }>>([{ value: "" }]);
    const [mapOpen, setMapOpen] = useState(false);
    const [mapAddress, setMapAddress] = useState("");
    const [mapLatLon, setMapLatLon] = useState("");
    const [addressSuggestions, setAddressSuggestions] = useState<SupplierAddressSuggestion[]>([]);
    const [supplierImages, setSupplierImages] = useState<Partial<Record<SupplierImageField, FileEntity>>>({});
    const latLon = Form.useWatch("latLon", form);

    useEffect(() => {
        if (!id) {
            form.setFieldsValue({ supplierName: "", type: 1, legalRepresentative: "" });
            return;
        }
        void load(id)
            .then(result => {
                const supplier = normalizeSupplierFormValues(result.supplier as SupplierFormValues);
                form.setFieldsValue({
                    ...supplier,
                    areaCode: [supplier.provinceCode, supplier.cityCode, supplier.regionCode].filter(
                        (value): value is string => Boolean(value)
                    ),
                    labelsText: supplier.labels?.join(","),
                    latLon: supplier.geo ? [supplier.geo.lng || "", supplier.geo.lat || ""].join(",") : undefined
                });
                // 编辑态直接预加载行政区名称，避免 Cascader 在首屏只显示编码字符串。
                void loadAreas()
                    .then(options => {
                        setAreaOptions(options || []);
                        setAreaLoaded(true);
                    })
                    .catch(() => setErrorMessage("区域数据加载失败"));
                setEnterpriseTags(
                    supplier.labels?.length ? supplier.labels.map(value => ({ value })) : [{ value: "" }]
                );
                setIsRelateGroup(Boolean(supplier.groupCompanyId));
                setSelectedGroupName(supplier.groupCompanyName);
                const fileIds = SUPPLIER_IMAGE_FIELDS.map(field => Number(supplier[field])).filter(
                    value => Number.isFinite(value) && value > 0
                );
                if (fileIds.length) {
                    void loadFiles(fileIds)
                        .then(files =>
                            setSupplierImages(
                                Object.fromEntries(
                                    SUPPLIER_IMAGE_FIELDS.flatMap(field => {
                                        const file = files.find(item => String(item.id) === String(supplier[field]));
                                        return file ? [[field, file]] : [];
                                    })
                                )
                            )
                        )
                        .catch(() => setErrorMessage("供应商图片加载失败"));
                }
                if (supplier.groupCompanyId && supplier.groupCompanyName) {
                    setGroupSearch(supplier.groupCompanyName);
                    void loadGroups(supplier.groupCompanyName)
                        .then(setGroupOptions)
                        .catch(() => {
                            // 编辑回显集团候选失败时保留表单，并提示远程数据暂不可用。
                            setGroupOptions([]);
                            setErrorMessage("集团查询失败");
                        });
                }
            })
            .catch(() => setErrorMessage("供应商详情加载失败"));
    }, [form, id, load, loadFiles, loadGroups]);

    /** 仅在用户展开区域控件时加载行政区，保持 Vue 页面可用且不拖慢首屏。 */
    const ensureAreaOptions = async () => {
        if (areaLoaded || areaLoading) return;
        setAreaLoading(true);
        try {
            setAreaOptions((await loadAreas()) || []);
            setAreaLoaded(true);
        } catch {
            setErrorMessage("区域数据加载失败");
        } finally {
            setAreaLoading(false);
        }
    };

    /** 按旧 Vue 地址联想行为查询 POI，选中后回填地址和坐标。 */
    const searchAddresses = async (input: string) => {
        const keyword = input.trim();
        if (!keyword) {
            setAddressSuggestions([]);
            return;
        }
        try {
            setAddressSuggestions((await loadAddresses(keyword, form.getFieldValue("regionCode"))) || []);
        } catch {
            setAddressSuggestions([]);
            setErrorMessage("地址联想查询失败");
        }
    };

    /** 选中旧 Vue 自动完成项后回填地址、坐标，并在未选区域时补齐行政区编码。 */
    const selectAddressSuggestion = (value: string) => {
        const suggestion = addressSuggestions.find(item => item.value === value || item.address === value);
        if (!suggestion) return;
        const areaCode =
            !form.getFieldValue("regionCode") && suggestion.countyCode ? sliceGbCode(suggestion.countyCode) : [];
        form.setFieldsValue({
            address: suggestion.address || suggestion.value,
            latLon: suggestion.lonlat,
            ...(areaCode.length
                ? {
                      areaCode,
                      provinceCode: areaCode[0],
                      cityCode: areaCode[1],
                      regionCode: areaCode[2]
                  }
                : {})
        });
        setAddressSuggestions([]);
    };

    // 沿用 Vue remote select：空关键词不请求集团 selector。
    const searchGroups = async (name: string) => {
        setGroupSearch(name);
        if (!name.trim()) {
            setGroupOptions([]);
            return;
        }
        try {
            setGroupOptions((await loadGroups(name)) || []);
        } catch {
            // 集团候选查询失败时清空旧选项，避免误选，并反馈接口异常。
            setGroupOptions([]);
            setErrorMessage("集团查询失败");
        }
    };

    /** 使用 zhihao Upload 完成旧 Vue ImgList 的上传、替换与回填。 */
    const requestSupplierImage = async (field: SupplierImageField, options: UploadRequestOptions) => {
        try {
            const uploaded = await uploadImage(options.file);
            form.setFieldsValue({ [field]: uploaded.id } as Partial<SupplierFormValues>);
            setSupplierImages(current => ({ ...current, [field]: uploaded }));
            setErrorMessage(null);
            options.onSuccess(uploaded);
        } catch (error) {
            setErrorMessage("供应商附件上传失败");
            options.onError(error instanceof Error ? error : new Error("供应商附件上传失败"));
        }
    };

    /** 删除图片时同步清理预览和提交字段，营业执照仍由表单规则保证必填。 */
    const removeSupplierImage = (field: SupplierImageField) => {
        form.setFieldsValue({ [field]: undefined } as Partial<SupplierFormValues>);
        setSupplierImages(current => {
            const next = { ...current };
            delete next[field];
            return next;
        });
    };

    /** 旧版图片上传卡：空态可上传，有值时支持预览、重新上传和删除。 */
    const renderImageUpload = (field: SupplierImageField, label: string, emptyText = "上传图片") => {
        const image = supplierImages[field];
        return (
            <ZhUpload
                accept="image/jpeg,image/png"
                action="#"
                aria-label={`上传${label}`}
                beforeUpload={file => validateSupplierImage(file as File)}
                httpRequest={options => void requestSupplierImage(field, options)}
                limit={1}
                showFileList={false}
            >
                <div
                    className="supplier-image-upload-panel"
                    data-testid={`supplier-upload-${field}`}
                    style={{
                        alignItems: "center",
                        background: "#ebf2fe",
                        border: image ? "none" : "1px dashed #85b1ff",
                        borderRadius: 4,
                        color: "var(--ant-color-primary)",
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        height: "136px",
                        justifyContent: "center",
                        overflow: "hidden",
                        position: "relative",
                        width: "240px"
                    }}
                >
                    {image ? (
                        <>
                            {image.path ? (
                                <Image
                                    alt={`${label}预览`}
                                    preview
                                    src={image.path}
                                    style={{ height: "136px", objectFit: "contain", width: "240px" }}
                                />
                            ) : (
                                <span>已上传图片</span>
                            )}
                            <Button
                                aria-label={`删除${label}`}
                                size="small"
                                style={{ position: "absolute", right: 2, top: 2 }}
                                onClick={event => {
                                    event.stopPropagation();
                                    removeSupplierImage(field);
                                }}
                            >
                                删除
                            </Button>
                            <span
                                style={{
                                    background: "rgba(0, 0, 0, 0.2)",
                                    bottom: 0,
                                    color: "#fff",
                                    left: 0,
                                    lineHeight: "30px",
                                    position: "absolute",
                                    textAlign: "center",
                                    width: "100%"
                                }}
                            >
                                重新上传
                            </span>
                        </>
                    ) : (
                        <>
                            <i aria-hidden="true" className="iconfont icon-shangchuan" />
                            <span>{emptyText}</span>
                        </>
                    )}
                </div>
            </ZhUpload>
        );
    };

    const submit = async () => {
        try {
            const values = Object.fromEntries(
                Object.entries(await form.validateFields()).filter(([, value]) => value !== undefined)
            ) as SupplierFormValues;
            // 级联控件只是编辑辅助字段，Vue 接口仍只接收 province/city/region 三个编码。
            delete values.areaCode;
            const submitValues: SupplierFormValues = isRelateGroup
                ? {
                      ...values,
                      groupCompanyId: Number(values.groupCompanyId),
                      groupCompanyName: selectedGroupName
                  }
                : (() => {
                      const unlinkedValues = { ...values };
                      delete unlinkedValues.groupCompanyId;
                      delete unlinkedValues.groupCompanyName;
                      return unlinkedValues;
                  })();
            if (submitValues.latLon) {
                const [lng, lat] = submitValues.latLon.split(",");
                submitValues.geo = { lng, lat };
                delete submitValues.latLon;
            }
            // Vue 接口接收标签数组，React 表单使用逗号文本便于快速录入后再统一转换。
            if (submitValues.labelsText !== undefined) {
                const labels = submitValues.labelsText
                    .split(",")
                    .map(label => label.trim())
                    .filter(Boolean);
                delete submitValues.labelsText;
                if (labels.length > 0) {
                    submitValues.labels = labels;
                } else {
                    delete submitValues.labels;
                }
            } else {
                const labels = enterpriseTags.map(tag => tag.value.trim()).filter(Boolean);
                if (labels.length > 0) submitValues.labels = labels;
            }
            await save(id, submitValues);
            void navigate("/supplierManagement/enterpriseManage");
        } catch {
            setErrorMessage("供应商保存失败");
        }
    };

    return (
        <section
            aria-label="supplier-form-page"
            className="consignment-form-page supplier-form-page detail-page min-h-full"
        >
            <section className="section-card consignment-form__header">
                <DetailPageHeader title={id ? "修改供应商" : "新增供应商"} />
            </section>
            {errorMessage ? <Alert className="mb-4" role="alert" title={errorMessage} type="error" /> : null}
            <Form colon={false} form={form} className="form large-form" labelCol={{ flex: "135px" }}>
                {/* 这些隐藏字段仅承载旧接口编码，不在视觉上重复展示区域与文件 ID。 */}
                <Form.Item hidden name="provinceCode" rules={[{ required: true, message: "请选择省份" }]}>
                    <Input aria-label="省份编码" />
                </Form.Item>
                <Form.Item hidden name="cityCode" rules={[{ required: true, message: "请选择地级市" }]}>
                    <Input aria-label="城市编码" />
                </Form.Item>
                <Form.Item hidden name="regionCode" rules={[{ required: true, message: "请选择县区" }]}>
                    <Input aria-label="区域编码" />
                </Form.Item>
                <Form.Item hidden name="latLon" rules={[{ required: true, message: "请选择经纬度" }]}>
                    <Input aria-label="经纬度" />
                </Form.Item>
                <Form.Item hidden name="labelsText">
                    <Input aria-label="企业标签" />
                </Form.Item>
                {SUPPLIER_IMAGE_FIELDS.map(field => (
                    <Form.Item
                        key={field}
                        hidden
                        name={field}
                        rules={field === "businessFileId" ? [{ required: true, message: "请上传营业执照" }] : []}
                    >
                        <Input aria-label={SUPPLIER_IMAGE_FIELD_LABELS[field]} />
                    </Form.Item>
                ))}

                <div className="section-card mb-8">
                    <div className="section-title">基本信息</div>
                    <Row align="middle" gutter={32}>
                        <Col {...RESPONSIVE_FORM_COL} className="supplier-form-responsive-col">
                            <Form.Item label="是否关联集团" required>
                                <Radio.Group
                                    value={isRelateGroup ? 1 : 0}
                                    options={[
                                        { label: "是", value: 1 },
                                        { label: "否", value: 0 }
                                    ]}
                                    onChange={event => {
                                        const related = event.target.value === 1;
                                        setIsRelateGroup(related);
                                        if (!related) {
                                            form.setFieldsValue({
                                                groupCompanyId: undefined,
                                                groupCompanyName: undefined
                                            });
                                            setGroupSearch("");
                                            setSelectedGroupName(undefined);
                                            setGroupOptions([]);
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        {isRelateGroup ? (
                            <Col {...RESPONSIVE_FORM_COL} className="supplier-form-responsive-col">
                                <Form.Item
                                    label="集团"
                                    name="groupCompanyId"
                                    required
                                    rules={[{ required: true, message: "请选择集团" }]}
                                >
                                    <Select
                                        allowClear
                                        aria-label="集团"
                                        filterOption={false}
                                        options={groupOptions.map(option => ({
                                            label: option.name,
                                            value: Number(option.id)
                                        }))}
                                        placeholder="请输入关键词以搜索集团"
                                        searchValue={groupSearch}
                                        showSearch
                                        onSearch={value => void searchGroups(value)}
                                        onChange={value => {
                                            const selected = groupOptions.find(option => Number(option.id) === value);
                                            setSelectedGroupName(selected?.name);
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        ) : null}
                        <Col {...RESPONSIVE_FORM_COL} className="supplier-form-responsive-col">
                            <Form.Item
                                label="供应商名称"
                                name="supplierName"
                                required
                                rules={[
                                    { required: true, message: "请输入供应商名称" },
                                    { max: 32, message: "不超过32位字符" }
                                ]}
                            >
                                <Input allowClear aria-label="供应商名称" placeholder="请输入供应商名称" />
                            </Form.Item>
                        </Col>
                        <Col {...RESPONSIVE_FORM_COL} className="supplier-form-responsive-col">
                            <Form.Item
                                label="类型"
                                name="type"
                                required
                                rules={[{ required: true, message: "请选择类型" }]}
                            >
                                <Select
                                    aria-label="类型"
                                    options={ENTERPRISE_SUPPLIER_TYPE.options}
                                    placeholder="请选择类型"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row align="middle" gutter={32}>
                        <Col {...RESPONSIVE_FORM_COL} className="supplier-form-responsive-col">
                            <Form.Item label="展示位置" name="showLocation">
                                <Select
                                    allowClear
                                    aria-label="展示位置"
                                    options={ENTERPRISE_SHOW_LOCATION.options}
                                    placeholder="请选择展示位置"
                                />
                            </Form.Item>
                        </Col>
                        <Col {...RESPONSIVE_FORM_COL} className="supplier-form-responsive-col">
                            <Form.Item label="所在区域" name="areaCode" required>
                                <Cascader
                                    aria-label="所在区域（级联）"
                                    loading={areaLoading}
                                    options={areaOptions}
                                    placeholder="请选择"
                                    showSearch
                                    onOpenChange={open => open && void ensureAreaOptions()}
                                    onChange={value => {
                                        const codes = value.map(String);
                                        form.setFieldsValue({
                                            areaCode: codes,
                                            provinceCode: codes[0],
                                            cityCode: codes[1],
                                            regionCode: codes[2],
                                            address: "",
                                            latLon: ""
                                        });
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col {...RESPONSIVE_FORM_COL} className="supplier-form-responsive-col">
                            <Form.Item
                                label=""
                                name="address"
                                required
                                rules={[{ required: true, message: "请输入详细地址" }]}
                            >
                                <AutoComplete
                                    aria-label="详细地址"
                                    options={addressSuggestions.map(item => ({
                                        label:
                                            item.address && item.address !== item.value
                                                ? `${item.value} ${item.address}`
                                                : item.value,
                                        value: item.address || item.value
                                    }))}
                                    placeholder="请输入详细地址"
                                    onSearch={value => void searchAddresses(value)}
                                    onSelect={selectAddressSuggestion}
                                >
                                    <Input allowClear maxLength={200} />
                                </AutoComplete>
                            </Form.Item>
                        </Col>
                        <Col {...RESPONSIVE_FORM_COL} className="supplier-form-responsive-col">
                            <Form.Item label="">
                                <Input
                                    aria-label="经纬度显示"
                                    disabled
                                    placeholder="请选择经纬度"
                                    value={latLon || ""}
                                    suffix={
                                        <Button
                                            aria-label="地图选点"
                                            size="small"
                                            type="text"
                                            onClick={() => {
                                                setMapAddress(form.getFieldValue("address") || "");
                                                setMapLatLon(form.getFieldValue("latLon") || "");
                                                setMapOpen(true);
                                            }}
                                        >
                                            <i aria-hidden="true" className="iconfont icon-map" />
                                        </Button>
                                    }
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="企业标签">
                        <div aria-label="企业标签编辑器" className="flex flex-wrap items-center gap-3">
                            {enterpriseTags.map((tag, index) => (
                                <span key={`enterprise-tag-${index}`} className="relative inline-flex">
                                    <Input
                                        aria-label={`企业标签-${index + 1}`}
                                        maxLength={6}
                                        placeholder="请输入企业标签"
                                        showCount
                                        value={tag.value}
                                        onChange={event => {
                                            const value = event.target.value;
                                            setEnterpriseTags(current =>
                                                current.map((currentTag, currentIndex) =>
                                                    currentIndex === index ? { value } : currentTag
                                                )
                                            );
                                        }}
                                    />
                                    <Button
                                        aria-label={`删除企业标签-${index + 1}`}
                                        className="supplier-form__tag-remove"
                                        size="small"
                                        onClick={() =>
                                            setEnterpriseTags(current =>
                                                current.length > 1
                                                    ? current.filter((_, currentIndex) => currentIndex !== index)
                                                    : current
                                            )
                                        }
                                    >
                                        <i aria-hidden="true" className="iconfont icon-close" />
                                    </Button>
                                </span>
                            ))}
                            <Button
                                aria-label="添加企业标签"
                                disabled={enterpriseTags.length >= 5}
                                type="link"
                                onClick={() => setEnterpriseTags(current => [...current, { value: "" }])}
                            >
                                + 企业标签
                            </Button>
                        </div>
                    </Form.Item>
                    <Row align="middle" gutter={32}>
                        <Col {...RESPONSIVE_FORM_COL} className="supplier-form-responsive-col">
                            <Form.Item label="联系人" name="contactPerson">
                                <Input allowClear aria-label="联系人" placeholder="请输入联系人" />
                            </Form.Item>
                        </Col>
                        <Col {...RESPONSIVE_FORM_COL} className="supplier-form-responsive-col">
                            <Form.Item
                                label="联系电话"
                                name="contactPhone"
                                rules={[
                                    {
                                        validator: (_, value) =>
                                            !value || validatePhone(String(value))
                                                ? Promise.resolve()
                                                : Promise.reject(new Error("请输入正确的手机号"))
                                    }
                                ]}
                            >
                                <Input allowClear aria-label="联系电话" placeholder="请输入联系电话" />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                <div className="section-card">
                    <div className="section-title">企业信息</div>
                    <Row align="middle" gutter={32}>
                        <Col {...RESPONSIVE_FORM_COL} className="supplier-form-responsive-col">
                            <Form.Item
                                label="主营品类"
                                name="mainCategories"
                                required
                                rules={[
                                    { required: true, message: "请输入主营品类" },
                                    { max: 50, message: "不超过50位字符" }
                                ]}
                            >
                                <Input
                                    allowClear
                                    aria-label="主营品类"
                                    maxLength={50}
                                    placeholder="请输入主营品类"
                                    showCount
                                />
                            </Form.Item>
                        </Col>
                        <Col {...RESPONSIVE_FORM_COL} className="supplier-form-responsive-col">
                            <Form.Item
                                label="统一社会信用代码"
                                name="socialCreditCode"
                                required
                                rules={[
                                    { required: true, message: "请输入统一社会信用代码" },
                                    {
                                        pattern: /^[A-Z0-9]{18}$/,
                                        message: "请输入有效的18位统一社会信用代码"
                                    }
                                ]}
                            >
                                <Input allowClear aria-label="统一社会信用代码" placeholder="请输入统一社会信用代码" />
                            </Form.Item>
                        </Col>
                        <Col {...RESPONSIVE_FORM_COL} className="supplier-form-responsive-col">
                            <Form.Item
                                label="法人姓名"
                                name="legalRepresentative"
                                required
                                rules={[
                                    { required: true, message: "请输入法人姓名" },
                                    { max: 10, message: "不超过10位字符" }
                                ]}
                            >
                                <Input allowClear aria-label="法人姓名" placeholder="请输入法人姓名" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row align="middle" gutter={32}>
                        <Col {...RESPONSIVE_FORM_COL} className="supplier-form-responsive-col">
                            <Form.Item className="supplier-form__image-item" label="企业logo" required={false}>
                                {renderImageUpload("logoFileId", "企业logo")}
                            </Form.Item>
                        </Col>
                        <Col {...RESPONSIVE_FORM_COL} className="supplier-form-responsive-col">
                            <Form.Item className="supplier-form__image-item" label="营业执照" required>
                                {renderImageUpload("businessFileId", "营业执照")}
                            </Form.Item>
                        </Col>
                        <Col className="supplier-form-responsive-col" span={12}>
                            <Form.Item className="supplier-form__image-item" label="供应商首页广告">
                                <Space align="start" wrap>
                                    {renderImageUpload("mobileSideFileId", "移动端首页广告", "上传图片-移动端")}
                                    {renderImageUpload("pcSideFileId", "PC端首页广告", "上传图片-PC端")}
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="介绍" name="description">
                        <Input.TextArea
                            allowClear
                            aria-label="介绍"
                            maxLength={300}
                            placeholder="请输入介绍"
                            rows={3}
                            showCount
                            style={{ resize: "none" }}
                        />
                    </Form.Item>
                </div>
                <Space className="operation-fixed-btn" data-testid="supplier-form-fixed-actions" size={0}>
                    <Button aria-label="提交" type="primary" onClick={() => void submit()}>
                        提交
                    </Button>
                    <Button aria-label="返回" onClick={() => void navigate("/supplierManagement/enterpriseManage")}>
                        返回
                    </Button>
                </Space>
            </Form>
            <ZhDialog
                open={mapOpen}
                title="供应商位置选择"
                width="860px"
                footer={
                    <Space>
                        <Button onClick={() => setMapOpen(false)}>取消</Button>
                        <Button
                            type="primary"
                            onClick={() => {
                                form.setFieldsValue({ address: mapAddress, latLon: mapLatLon });
                                setMapOpen(false);
                            }}
                        >
                            确定位置
                        </Button>
                    </Space>
                }
                onCancel={() => setMapOpen(false)}
            >
                <ZhGrid cols={1} gap={[12, 12]}>
                    <ZhBaseItem label="当前坐标" value={mapLatLon || "未选择"} />
                    <Input
                        aria-label="地图地址"
                        placeholder="请输入地图地址"
                        value={mapAddress}
                        onChange={event => setMapAddress(event.target.value)}
                    />
                    <Input
                        aria-label="地图经纬度"
                        placeholder="请输入经度,纬度"
                        value={mapLatLon}
                        onChange={event => setMapLatLon(event.target.value)}
                    />
                </ZhGrid>
                <ZhMap center={parseMapCenter(mapLatLon)} className="mt-4 h-80 w-full" showScale zoom={13} />
            </ZhDialog>
        </section>
    );
}

export default SupplierFormPage;
